import { FC, ReactNode } from 'react';
import { useMutation } from '@apollo/client';
import { Flex, useDisclosure } from '@chakra-ui/react';

import cashImg from 'assets/brand/cash.png';
import useCompanyContext from 'common/hooks/useCompanyContext';
import Button from 'components/_core/Button';
import SunshineIcon from 'components/_core/SunshineIcon';
import SunshineModal, {
  SunshineModalProps,
} from 'components/_core/SunshineModal';
import Text from 'components/_core/Text';
import { logEvent } from 'features/Analytics/analytics';

import { AskForFreeMonthDocument } from './askForFreeMonth.gql';
import ConfirmationModal from './components/ConfirmationModal';
import locales from './locales';

interface ItemProps {
  children: ReactNode;
}

const Item: FC<ItemProps> = ({ children }) => (
  <Text columnGap="space-16" display="flex" variant="primary">
    <SunshineIcon name="tick" size="icon-20" />
    {children}
  </Text>
);

interface PricingIssueModalProps extends Omit<SunshineModalProps, 'children'> {
  onCloseLink?: () => void;
}

const PricingIssueModal: FC<PricingIssueModalProps> = (props) => {
  const { onClose, onCloseLink } = props;
  const { companyProfileId, companyUserId } = useCompanyContext();
  const { onOpen, ...modalProps } = useDisclosure();

  if (!companyProfileId || !companyUserId) {
    throw new Error('companyProfileId or companyUserId cannot be undefined');
  }

  const [askForFreeMonthMutation] = useMutation(AskForFreeMonthDocument);
  const onConfirm = async () => {
    await askForFreeMonthMutation({
      variables: { input: { companyProfileId, companyUserId } },
    });
    logEvent({ name: `Account Closure Discount Accepted` });
    onClose();
    onOpen();
  };

  const onCancel = () => {
    logEvent({ name: `Account Closure Discount Refused` });
    onClose();
    if (onCloseLink) {
      onCloseLink();
    }
  };

  return (
    <>
      <SunshineModal {...props}>
        <SunshineModal.Header />

        <SunshineModal.Body>
          <SunshineModal.Body.Image src={cashImg} />

          <SunshineModal.Title>{locales.title}</SunshineModal.Title>
          <Flex flexDirection="column" gap={16}>
            <Text variant="primary">{locales.description}</Text>
            {locales.items.map((item) => (
              <Item key={item}>{item}</Item>
            ))}
          </Flex>
        </SunshineModal.Body>

        <SunshineModal.Footer>
          <Button onClick={onCancel} variant="inline-secondary">
            {locales.cancel}
          </Button>

          <Button onClick={onConfirm}>{locales.confirm}</Button>
        </SunshineModal.Footer>
      </SunshineModal>

      <ConfirmationModal {...modalProps} />
    </>
  );
};

export default PricingIssueModal;
